import type { Recurso } from '../../api/ops/pagos';
import type { Decoracion, Posicion } from '../../api/ops/sala';
import { CapaDecoracion } from '../plano/CapaDecoracion';
import { cajaDePieza } from '../plano/decoracion';
import { horaEn } from '../reservas/fechas';
import { cajaDe, type EstadoPintado } from './logica';

/**
 * El plano de una sala (8.7): cada recurso en su sitio, con su forma y su estado escrito (Libre, la hora, Tarde, Sentada,
 * Retén, Cerrado), no solo con color. Medidas en la rejilla del plano (1 a 10.000), escaladas al hueco con porcentajes.
 * Los que encajan con la reserva elegida se marcan; los elegidos para sentar, en rojo. La decoración de la versión en vigor
 * se ve debajo, sin poder tocarla y sin las plantas (G882, 16-oct).
 */
export interface RecursoEnPlano {
    posicion: Posicion;
    recurso: Recurso | undefined;
    estado: EstadoPintado;
    proxima: string | null;
}

interface Props {
    nombre: string;
    ancho: number;
    alto: number;
    recursos: readonly RecursoEnPlano[];
    decoracion: readonly Decoracion[];
    zona: string;
    zoom: number;
    encajan: ReadonlySet<string>;
    elegidos: ReadonlySet<string>;
    onRecurso: (r: RecursoEnPlano) => void;
}

const TEXTO: Readonly<Record<EstadoPintado, string>> = {
    libre: 'Libre',
    reservado: 'Reservada',
    tarde: 'Tarde',
    sentado: 'Sentada',
    reten: 'Retén',
    cerrado: 'Cerrado',
};

export function PlanoSala({ nombre, ancho, alto, recursos, decoracion, zona, zoom, encajan, elegidos, onRecurso }: Props) {
    const caja = cajaDe([...recursos.map((r) => r.posicion), ...decoracion.map(cajaDePieza)], ancho, alto);
    const pct = (v: number, total: number) => `${String((v / total) * 100)}%`;
    return (
        <div className="rv-sl-plano" style={{ width: `${String(Math.round(zoom * 100))}%` }}>
            <div className="rv-sl-plano__lienzo" role="group" aria-label={`Plano de ${nombre}`} style={{ aspectRatio: `${String(caja.ancho)} / ${String(caja.alto)}` }}>
                <CapaDecoracion ancho={caja.ancho} alto={caja.alto} piezas={decoracion.map((pieza, indice) => ({ indice, pieza }))} enVivo />
                {recursos.map((r) => {
                    const p = r.posicion;
                    const codigo = p.label ?? r.recurso?.code ?? '';
                    const hora = r.proxima === null ? null : horaEn(r.proxima, zona);
                    const estado = r.estado === 'reservado' && hora !== null ? `Reservada a las ${hora}` : TEXTO[r.estado];
                    const clases = [
                        'rv-sl-recurso',
                        `rv-sl-recurso--${p.shape}`,
                        `rv-sl-recurso--${r.estado}`,
                        encajan.has(p.resource_id) ? 'rv-sl-recurso--encaja' : '',
                        elegidos.has(p.resource_id) ? 'rv-sl-recurso--elegido' : '',
                    ].filter((c) => c !== '').join(' ');
                    return (
                        <button
                            key={p.resource_id}
                            type="button"
                            className={clases}
                            aria-pressed={elegidos.has(p.resource_id)}
                            aria-label={`${codigo}: ${estado}${r.recurso === undefined ? '' : ` · ${String(r.recurso.current_terms.min_capacity)}–${String(r.recurso.current_terms.max_capacity)} personas`}`}
                            style={{
                                left: pct(p.x, caja.ancho),
                                top: pct(p.y, caja.alto),
                                width: pct(p.width, caja.ancho),
                                height: pct(p.height, caja.alto),
                                transform: p.rotation === undefined || p.rotation === 0 ? undefined : `rotate(${String(p.rotation)}deg)`,
                            }}
                            onClick={() => { onRecurso(r); }}
                        >
                            <strong>{codigo}</strong>
                            {r.estado === 'reservado' && hora !== null && <small>{hora}</small>}
                            {r.estado !== 'reservado' && r.estado !== 'libre' && <small>{TEXTO[r.estado]}</small>}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
